import React, { useState, useMemo } from 'react';
import { CalendarBlank, MonitorPlay } from '@phosphor-icons/react';
import classNames from 'classnames';

import { Button } from '../../components/general';
import { PageWrapper } from '../../components/layout/content';

import { isEmpty } from '../../utils';

import ProjectsTab from '../Dashboard/components/projectsTab';
import AchievementBadge from './components/achievementBadge';
import AttendeePanel from './components/attendeePanel';
import EventBlock from './components/eventBlock';
import { ExtendedEvent, getEvents, getAssetsFile } from './components/getData';

const AttendeePortal = () => {
  const name = 'Naomi Henn';

  const attendeeDetails = [
    { label: 'Name', value: name },
    { label: 'Email', value: 'naomi@crowdclip.com' },
    { label: 'Instagram', value: '@naomisuits' },
  ];

  const { completedEvents, attentionRequiredEvents, inProgressEvents } =
    useMemo(() => {
      const completedEvents: ExtendedEvent[] = [];
      const attentionRequiredEvents: ExtendedEvent[] = [];
      const inProgressEvents: ExtendedEvent[] = [];

      getEvents().forEach((event) => {
        if (event.stage === 'Generation') completedEvents.push(event);
        else if (event.stage === 'Analysis' && isEmpty(event.clips))
          attentionRequiredEvents.push(event);
        else inProgressEvents.push(event);
      });

      return { completedEvents, attentionRequiredEvents, inProgressEvents };
    }, []);

  const tabs = [
    {
      label: 'Your Attention Required',
      value: 'attention-required',
      eventsList: attentionRequiredEvents,
    },
    {
      label: 'Highlight Reel is ready',
      value: 'completed',
      eventsList: completedEvents,
    },
    {
      label: 'In progress',
      value: 'in-progress',
      eventsList: inProgressEvents,
    },
  ];

  const [pageTab, setPageTab] = useState(
    tabs.find((tab) => !isEmpty(tab.eventsList))?.value ?? '',
  );

  const events = tabs.find((tab) => tab.value === pageTab)?.eventsList ?? [];

  const photoClass =
    'w-[calc((100%_-_(1rem_*_5))_/_6)] aspect-square rounded-full object-cover';

  return (
    <PageWrapper type="header-only" title="Attendee Portal">
      <div className="flex gap-10">
        <AttendeePanel
          heading="Attendee Card"
          className="sticky top-[calc(var(--header-height)_+_var(--page-spacing))] w-1/4 h-[calc(100dvh_-_var(--header-height)_-_(var(--page-spacing)_*_2))] gap-7 items-center overflow-y-scroll"
        >
          <hr className="w-full" />

          <img
            src={getAssetsFile('photo-1.jpg')}
            className="w-52 aspect-square rounded-full object-cover"
            alt={`${name} Icon`}
          />

          <div className="w-full flex flex-col gap-4">
            {attendeeDetails.map((item) => (
              <div className="leading-tight" key={item.label}>
                <div className="font-semibold">{item.label}</div>
                <div>{item.value}</div>
              </div>
            ))}
          </div>

          <div className="w-full flex flex-col gap-2">
            <div className="font-semibold">Achievements</div>

            <div className="flex flex-wrap gap-4">
              <AchievementBadge
                Icon={CalendarBlank}
                num={
                  completedEvents.length +
                  attentionRequiredEvents.length +
                  inProgressEvents.length
                }
                description="Event Check-Ins"
              />

              <AchievementBadge
                Icon={MonitorPlay}
                num={completedEvents.length}
                description="Videos Generated"
              />
            </div>
          </div>
        </AttendeePanel>

        <div className="w-3/4 flex flex-col gap-10">
          <AttendeePanel heading="Your Photos">
            <div className="w-full flex items-center gap-4">
              {Array.from({ length: 5 }, (_, i) => i + 1).map((index) => (
                <img
                  key={index}
                  src={getAssetsFile(`photo-${index}.jpg`)}
                  className={photoClass}
                  alt={`${name} Icon`}
                />
              ))}

              <Button
                variant="custom"
                colour="grey-outline-salmon"
                className={classNames(
                  photoClass,
                  'flex items-center justify-center text-2xl',
                )}
              >
                +
              </Button>
            </div>
          </AttendeePanel>

          <AttendeePanel heading="Your Events">
            <div className="flex gap-4">
              {tabs.map(
                (tab) =>
                  !isEmpty(tab.eventsList) && (
                    <ProjectsTab
                      key={tab.value}
                      label={tab.label}
                      onClick={() => setPageTab(tab.value)}
                      isCurrent={pageTab === tab.value}
                    />
                  ),
              )}
            </div>

            <div className="flex flex-wrap gap-7">
              {events.map((event) => (
                <EventBlock key={event.eventId} event={event} />
              ))}
            </div>
          </AttendeePanel>
        </div>
      </div>
    </PageWrapper>
  );
};

export default AttendeePortal;
